<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
  <nav aria-label="menu" class="bg-surface-default-low">
    <ul
      class="relative flex h-48 items-center justify-center gap-32 px-32"
      role="menubar"
    >
      <li
        class="flex h-full flex-nowrap gap-4 border-b-2 transition-all duration-700 ease-in-out hover:border-static-default-hi"
        :class="[
          isActivePage(GOOD_DEALS.url) && !isMenuActive
            ? 'border-static-default-hi'
            : 'border-transparent',
        ]"
        role="none"
      >
        <RevLink
          class="bold-hack border-static-default-low text-action-default-low box-content inline-block rounded-none transition-all duration-700 ease-in-out hover:body-2-bold hover:no-underline"
          :class="[
            isActivePage(GOOD_DEALS.url) && !isMenuActive
              ? 'body-2-bold'
              : 'body-2',
          ]"
          role="menuitem"
          :style="{ color: GOOD_DEALS.color }"
          :title="GOOD_DEALS.title"
          :to="GOOD_DEALS.url"
          :underlined="false"
        >
          <span class="flex h-full flex-nowrap items-center gap-4">
            <component :is="GOOD_DEALS.icon" v-if="GOOD_DEALS.icon" />
            <span>{{ GOOD_DEALS.title }}</span>
          </span>
        </RevLink>
      </li>

      <li
        v-for="item in items"
        :key="item.id"
        class="flex h-full flex-nowrap gap-4 border-b-2 transition-all duration-700 ease-in-out hover:border-static-default-hi"
        :class="[
          isActivePage(item.url) && !isMenuActive
            ? 'border-static-default-hi'
            : 'border-transparent',
        ]"
        role="none"
        @blur="hideMenu"
        @focus="() => showMenu(item.id)"
        @mouseout="hideMenu"
        @mouseover="() => showMenu(item.id)"
      >
        <RevLink
          :aria-expanded="String(activeMenu === item.id)"
          :aria-haspopup="String(item.children.length > 0)"
          class="bold-hack border-static-default-low text-action-default-low box-content inline-block rounded-none transition-all duration-700 ease-in-out hover:body-2-bold hover:no-underline"
          :class="[
            isActivePage(item.url) && !isMenuActive ? 'body-2-bold' : 'body-2',
          ]"
          role="menuitem"
          :style="{ color: item.color }"
          :title="item.title"
          :to="item.url"
          :underlined="false"
        >
          <span class="flex h-full flex-nowrap items-center gap-4">
            <component :is="item.icon" v-if="item.icon" />
            <span>{{ item.title }}</span>
          </span>
        </RevLink>
        <div
          v-show="activeMenu === item.id && item.children.length > 0"
          aria-label="submenu"
          class="absolute inset-x-0 mt-48 flex"
          :class="[isMenuActive ? 'border-static-default-low border-t' : '']"
        >
          <NavMenuSidebar
            v-if="features.buyback.enabled || features.mobilePlan.enabled"
            class="max-w-256"
            :mobilePlan="mobilePlan(item.title)"
            :tradeIn="tradeIn(item.title)"
          />
          <NavMenuContent
            class="flex-1"
            :items="item.children"
            :universeTitle="item.title"
            :universeURL="item.url"
          />
        </div>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevLink } from '@ds/components/Link'
import { IconSparkles } from '@ds/icons/IconSparkles'

import type { Features } from '~/features'

import { useNavigation } from '../../composables/useNavigation'

import t from './NavMenu.translations'
import NavMenuContent from './NavMenuContent.vue'
import NavMenuSidebar from './NavMenuSidebar.vue'

const { features } = useMarketplace<Features>()

const i18n = useI18n()
const locale = useI18nLocale()
const GOOD_DEALS = {
  title: i18n(t.goodDeals),
  children: [],
  id: -1,
  color: '#9D3963',
  icon: IconSparkles,
  url: `/${locale}/e/good-deals`,
}

const {
  items,
  setActiveMenu,
  clearActiveMenu,
  activeMenu,
  isMenuActive,
  isActivePage,
} = useNavigation()

const timeout = ref()
function showMenu(id: number) {
  clearTimeout(timeout.value)
  timeout.value = setTimeout(() => {
    setActiveMenu(id)
  }, 150)
}

function hideMenu() {
  clearTimeout(timeout.value)
  timeout.value = setTimeout(() => {
    clearActiveMenu()
  }, 150)
}

onBeforeUnmount(() => {
  clearTimeout(timeout.value)
})

const translations: Record<
  string,
  { id: string; defaultMessage: string } | undefined
> = t

const getTranslation = (key: string, universe: string) => {
  const translation = translations[`${key}_${universe.toLowerCase()}`]
  if (!translation) return undefined

  return i18n(translation)
}

const tradeIn = (universe: string) => {
  const title = getTranslation('tradeInTitle', universe)
  const description = getTranslation('tradeInDescription', universe)

  return title && description
    ? {
        title,
        description,
      }
    : undefined
}
const mobilePlan = (universe: string) => {
  const title = getTranslation('mobilePlanTitle', universe)
  const description = getTranslation('mobilePlanDescription', universe)

  return title && description
    ? {
        title,
        description,
      }
    : undefined
}
</script>

<style>
.bold-hack::after {
  display: block;
  content: attr(title);
  font-weight: 600;
  font-size: 0.875rem /* 14px */;
  line-height: 1.25rem /* 20px */;
  height: 0;
  overflow: hidden;
  visibility: hidden;
  contain: content;
}
</style>
